import React from 'react'
import { Search, Trash2, RefreshCw, ChevronLeft, ChevronRight, Mic } from 'lucide-react'
import { Button, Card, Input, Label } from '../ui'
import { cn } from '../../lib/utils'

const AdminSongsTab = ({
    searchQuery,
    setSearchQuery,
    allSongs,
    loadingSongs,
    songsPage,
    setSongsPage,
    songsPerPage,
    setSongsPerPage,
    totalSongPages,
    fetchSongs,
    fetchSongDetails,
    handleDeleteClick,
    deletingSongId,
    showRawData,
    setShowRawData,
    showLyricsModal,
    setShowLyricsModal,
    selectedSong,
    setSelectedSong,
    handleViewLyrics,
    formatDuration,
    formatDate
}) => {
    return (
        <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        type="text"
                        placeholder="Search songs by title or artist..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Button
                    type="button"
                    variant="glass"
                    size="icon"
                    onClick={() => fetchSongs(songsPage, songsPerPage, searchQuery)}
                >
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Songs Table */}
            <Card className="overflow-hidden border-white/10 bg-white/5 backdrop-blur-md">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-white/5">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">Artist</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">Duration</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">YouTube ID</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">Lyrics</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {loadingSongs ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : allSongs.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                        No songs found
                                    </td>
                                </tr>
                            ) : (
                                allSongs.map((song) => (
                                    <tr key={song.id} className="hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{song.id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white group-hover:text-primary-300 transition-colors">{song.title}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{song.artist}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{formatDuration(song.duration)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {song.youtubeId ? (
                                                <a
                                                    href={`https://youtube.com/watch?v=${song.youtubeId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    {song.youtubeId}
                                                </a>
                                            ) : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleViewLyrics(song.id)}
                                                className="text-primary-400 hover:text-primary-300 hover:bg-white/5 flex items-center gap-2"
                                            >
                                                <Mic className="h-4 w-4" />
                                                View Lyrics
                                            </Button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(song.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center space-x-2">
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="px-0 text-primary-600 hover:text-primary-900"
                                                    onClick={() => fetchSongDetails(song.id)}
                                                >
                                                    View Raw
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDeleteClick(song)}
                                                    disabled={deletingSongId === song.id}
                                                    title="Delete song and all associated data"
                                                    className="text-red-600 transition-colors hover:text-red-700"
                                                >
                                                    {deletingSongId === song.id ? (
                                                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-red-600"></div>
                                                    ) : (
                                                        <Trash2 className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                {(song.geminiRawResponse || song.geminiPrompt) && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        🤖 Gemini
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="bg-white/5 backdrop-blur-sm px-6 py-4 flex items-center justify-between border-t border-white/10">
                    <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-slate-400">Show</span>
                        <select
                            value={songsPerPage}
                            onChange={(e) => {
                                setSongsPerPage(Number(e.target.value))
                                setSongsPage(1)
                            }}
                            className="bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span className="text-sm font-medium text-slate-400">per page</span>
                    </div>

                    <div className="flex items-center space-x-4">
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setSongsPage(p => Math.max(1, p - 1))}
                            disabled={songsPage === 1}
                            className="h-10 w-10 bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <span className="text-sm font-semibold text-white min-w-[100px] text-center">
                            Page {songsPage} of {totalSongPages || 1}
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setSongsPage(p => Math.min(totalSongPages, p + 1))}
                            disabled={songsPage >= totalSongPages}
                            className="h-10 w-10 bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Raw Data Modal */}
            {showRawData && selectedSong && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/5">
                            <h3 className="text-xl font-bold text-white flex items-center gap-3">
                                <span className="bg-primary-500/20 p-2 rounded-lg"><RefreshCw className="h-5 w-5 text-primary-400" /></span>
                                Song Data & Gemini Debug Info - ID: {selectedSong.id}
                            </h3>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                    setShowRawData(false)
                                    setSelectedSong(null)
                                }}
                                className="text-slate-400 hover:text-white"
                            >
                                ✕
                            </Button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-10rem)] space-y-8 bg-slate-900/50">

                            {/* Gemini Debug Information */}
                            {(selectedSong.geminiRawResponse || selectedSong.geminiPrompt) && (
                                <div className="space-y-4">
                                    <h4 className="text-lg font-bold text-primary-400 flex items-center gap-2">
                                        <div className="w-1.5 h-6 bg-primary-500 rounded-full" />
                                        🤖 Gemini AI Debug Information
                                    </h4>

                                    {selectedSong.geminiPrompt && (
                                        <div className="bg-black/40 rounded-xl border border-white/5 overflow-hidden">
                                            <div className="px-4 py-2 bg-white/5 border-b border-white/5 text-xs font-bold text-slate-400 uppercase tracking-widest">Prompt Sent to Gemini</div>
                                            <pre className="p-4 overflow-x-auto text-sm text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">
                                                {selectedSong.geminiPrompt}
                                            </pre>
                                        </div>
                                    )}

                                    {selectedSong.geminiRawResponse && (
                                        <div className="bg-black/40 rounded-xl border border-white/5 overflow-hidden">
                                            <div className="px-4 py-2 bg-white/5 border-b border-white/5 text-xs font-bold text-slate-400 uppercase tracking-widest">Raw Response from Gemini</div>
                                            <pre className="p-4 overflow-x-auto text-sm text-green-400/90 whitespace-pre-wrap max-h-96 font-mono leading-relaxed">
                                                {selectedSong.geminiRawResponse}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Song Metadata */}
                            <div>
                                <h4 className="text-lg font-bold text-slate-200 flex items-center gap-2 mb-4">
                                    <div className="w-1.5 h-6 bg-slate-500 rounded-full" />
                                    📊 Song Metadata
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {[
                                        { label: 'Title', value: selectedSong.title },
                                        { label: 'Artist', value: selectedSong.artist || 'N/A' },
                                        { label: 'Duration', value: formatDuration(selectedSong.duration) },
                                        { label: 'YouTube ID', value: selectedSong.youtubeId },
                                        { label: 'Key', value: selectedSong.originalKey || 'N/A' },
                                        {
                                            label: 'Vocal Range',
                                            value: selectedSong.vocalRangeLow && selectedSong.vocalRangeHigh
                                                ? `${selectedSong.vocalRangeLow} - ${selectedSong.vocalRangeHigh}`
                                                : 'N/A'
                                        },
                                        { label: 'Processed', value: selectedSong.isProcessed ? 'Yes' : 'No' },
                                        { label: 'Created', value: formatDate(selectedSong.createdAt) }
                                    ].map((item, i) => (
                                        <div key={i} className="bg-black/30 p-4 rounded-xl border border-white/5">
                                            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{item.label}</div>
                                            <div className="text-white font-semibold">{item.value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Lyrics Preview */}
                            {selectedSong.lyrics && selectedSong.lyrics.length > 0 && (
                                <div>
                                    <h4 className="text-lg font-bold text-slate-200 flex items-center gap-2 mb-4">
                                        <div className="w-1.5 h-6 bg-slate-500 rounded-full" />
                                        🎵 Lyrics Preview ({selectedSong.lyrics.length} lines)
                                    </h4>
                                    <div className="bg-black/40 border border-white/5 p-4 rounded-xl max-h-64 overflow-y-auto font-mono">
                                        {selectedSong.lyrics.slice(0, 10).map((lyric, index) => (
                                            <div key={index} className="text-sm mb-2 flex items-start group">
                                                <span className="text-primary-500/60 w-24 flex-shrink-0 tabular-nums font-bold">
                                                    [{lyric.start_time?.toFixed(2)}s]
                                                </span>
                                                <span className="text-slate-300 group-hover:text-white transition-colors">{lyric.text}</span>
                                            </div>
                                        ))}
                                        {selectedSong.lyrics.length > 10 && (
                                            <div className="text-sm text-slate-500 italic mt-4 pt-4 border-t border-white/5 flex items-center gap-2">
                                                <div className="h-px flex-1 bg-white/5" />
                                                plus {selectedSong.lyrics.length - 10} more lines
                                                <div className="h-px flex-1 bg-white/5" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Processing Error */}
                            {selectedSong.processing_error && (
                                <div>
                                    <h4 className="text-lg font-bold text-red-400 flex items-center gap-2 mb-4">
                                        <div className="w-1.5 h-6 bg-red-500 rounded-full" />
                                        ❌ Processing Error
                                    </h4>
                                    <pre className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl overflow-x-auto text-sm whitespace-pre-wrap text-red-200 font-mono">
                                        {selectedSong.processing_error}
                                    </pre>
                                </div>
                            )}

                            {/* Raw JSON Data */}
                            <div>
                                <h4 className="text-lg font-bold text-slate-200 flex items-center gap-2 mb-4">
                                    <div className="w-1.5 h-6 bg-slate-500 rounded-full" />
                                    🔧 Complete Raw JSON Data
                                </h4>
                                <div className="bg-black/40 rounded-xl border border-white/5 overflow-hidden">
                                    <pre className="p-4 overflow-x-auto text-xs max-h-96 text-slate-400 font-mono leading-relaxed scrollbar-thin scrollbar-thumb-white/10">
                                        {JSON.stringify(selectedSong, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-white/10 bg-white/5">
                            <Button
                                type="button"
                                variant="outline"
                                className="bg-white/5 border-white/10 text-white hover:bg-white/10 px-8"
                                onClick={() => {
                                    setShowRawData(false)
                                    setSelectedSong(null)
                                }}
                            >
                                Close Inspector
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full Lyrics Modal */}
            {showLyricsModal && selectedSong && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/5">
                            <div className="flex items-center gap-4">
                                <div className="bg-primary-500/20 p-3 rounded-xl">
                                    <Mic className="h-6 w-6 text-primary-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-white uppercase tracking-tighter">
                                        {selectedSong.title}
                                    </h3>
                                    <p className="text-primary-400 font-bold text-sm">{selectedSong.artist || 'Unknown Artist'}</p>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                    setShowLyricsModal(false)
                                    setSelectedSong(null)
                                }}
                                className="text-slate-400 hover:text-white"
                            >
                                ✕
                            </Button>
                        </div>
                        <div className="p-8 overflow-y-auto flex-1 bg-slate-900/50 space-y-4">
                            {!selectedSong.lyrics || selectedSong.lyrics.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
                                    <div className="bg-white/5 p-6 rounded-full">
                                        <Mic className="h-12 w-12 text-slate-600" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-lg">No Lyrics Found</h4>
                                        <p className="text-slate-400 max-w-xs mx-auto">This song might still be processing or failed to extract lyrics.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {selectedSong.lyrics.map((lyric, index) => (
                                        <div key={index} className="flex items-start gap-6 group hover:bg-white/5 p-3 rounded-xl transition-all border border-transparent hover:border-white/5">
                                            <div className="flex flex-col items-end w-20 flex-shrink-0 pt-1">
                                                <span className="text-[10px] font-black text-primary-500/40 uppercase tracking-widest leading-none">Start</span>
                                                <span className="text-sm font-mono font-bold text-primary-400 tabular-nums">
                                                    {lyric.start_time?.toFixed(2)}s
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-lg font-medium text-slate-200 group-hover:text-white leading-snug">
                                                    {lyric.text}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-start w-20 flex-shrink-0 pt-1">
                                                <span className="text-[10px] font-black text-slate-500/40 uppercase tracking-widest leading-none">End</span>
                                                <span className="text-sm font-mono font-bold text-slate-400 tabular-nums">
                                                    {lyric.end_time?.toFixed(2)}s
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="p-6 border-t border-white/10 bg-white/5 flex items-center justify-between">
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                Total Lines: <span className="text-white ml-1">{selectedSong.lyrics?.length || 0}</span>
                            </div>
                            <Button
                                type="button"
                                variant="glass"
                                className="px-8 font-bold"
                                onClick={() => {
                                    setShowLyricsModal(false)
                                    setSelectedSong(null)
                                }}
                            >
                                Close View
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminSongsTab
